import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, ArrowLeft, LayoutGrid, Shirt } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SettingsProvider } from "../context/SettingsContext";
import { club } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROW_LABELS = ["Goalkeepers", "Defenders", "Midfielders", "Forwards"];

const posRow = (pos = "") => {
  const p = pos.toLowerCase();
  if (p.includes("goal") || p === "gk") return 0;
  if (p.includes("def") || p.includes("back")) return 1;
  if (p.includes("forward") || p.includes("strik") || p.includes("wing") || p.includes("attack")) return 3;
  return 2; // midfield / undecided / unknown
};

function Avatar({ p, size = "w-12 h-12" }) {
  if (p.photoUrl) {
    return <img src={p.photoUrl} alt={p.name} className={`${size} rounded-full object-cover border-2 border-gold/50`} />;
  }
  return (
    <div className={`${size} rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center`}>
      <img src={club.crest} alt="" className="w-1/2 h-1/2 object-contain opacity-80" />
    </div>
  );
}

function ListView({ players }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {players.map((p, i) => (
        <div key={`${p.name}-${i}`} className="group bg-card border border-white/10 rounded-lg p-5 hover:border-gold/50 transition-colors" data-testid={`team-player-${i}`}>
          <div className="mb-4"><Avatar p={p} /></div>
          <p className="text-white font-medium leading-tight">{p.name}</p>
          <p className="cond-font uppercase tracking-widest text-[11px] text-white/45 mt-1">{p.position || "Player"}</p>
        </div>
      ))}
    </div>
  );
}

function FormationView({ players }) {
  const rows = [[], [], [], []];
  players.forEach((p) => rows[posRow(p.position)].push(p));
  return (
    <div
      className="relative rounded-xl border border-white/10 overflow-hidden py-8 px-4"
      style={{ background: "linear-gradient(180deg,#0f3b1e 0%,#0c2e18 100%)" }}
      data-testid="formation-pitch"
    >
      {/* pitch markings */}
      <div className="absolute inset-4 border-2 border-white/15 rounded-md pointer-events-none" />
      <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t-2 border-white/15 pointer-events-none" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-2 border-white/15 rounded-full pointer-events-none" />
      <div className="relative space-y-8">
        {rows.map((group, ri) => (
          <div key={ri}>
            <p className="text-center cond-font uppercase tracking-[0.25em] text-white/40 text-[10px] mb-3">{ROW_LABELS[ri]}</p>
            {group.length === 0 ? (
              <p className="text-center text-white/25 text-xs font-light">—</p>
            ) : (
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-5">
                {group.map((p, i) => (
                  <div key={`${ri}-${i}`} className="flex flex-col items-center w-24 text-center" data-testid={`formation-player-${ri}-${i}`}>
                    <Avatar p={p} size="w-14 h-14" />
                    <span className="text-white text-xs font-medium mt-2 leading-tight">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Teams() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");

  useEffect(() => {
    axios
      .get(`${API}/public/roster`)
      .then((res) => setGroups(res.data.groups || []))
      .catch((e) => { console.error("Failed to load roster", e); setGroups([]); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <SettingsProvider>
      <div className="bg-background min-h-screen">
        <Navbar />

        <section className="relative pt-40 pb-16 bg-[#0d0d0f] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-5">
            <p className="cond-font uppercase tracking-[0.3em] text-gold text-sm mb-3">The Squad</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none">OUR TEAMS</h1>
            <p className="text-white/60 font-light mt-4 max-w-2xl">
              Meet the players representing Atlético South Florida across our seasons. Black · Gold · Ambition.
            </p>
            <div className="flex items-center justify-between flex-wrap gap-4 mt-6">
              <a href="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold transition-colors cond-font uppercase tracking-widest text-xs" data-testid="teams-back-home">
                <ArrowLeft size={15} /> Back to home
              </a>
              {groups.length > 0 && (
                <div className="inline-flex rounded-md border border-white/15 overflow-hidden" data-testid="teams-view-toggle">
                  <button onClick={() => setView("list")} className={`inline-flex items-center gap-2 cond-font uppercase tracking-widest text-xs px-4 py-2 transition-colors ${view === "list" ? "bg-gold text-black" : "text-white/60 hover:text-white"}`} data-testid="teams-view-list">
                    <LayoutGrid size={14} /> List
                  </button>
                  <button onClick={() => setView("formation")} className={`inline-flex items-center gap-2 cond-font uppercase tracking-widest text-xs px-4 py-2 transition-colors ${view === "formation" ? "bg-gold text-black" : "text-white/60 hover:text-white"}`} data-testid="teams-view-formation">
                    <Shirt size={14} /> Formation
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-5">
            {loading ? (
              <p className="text-white/40 font-light" data-testid="teams-loading">Loading squad…</p>
            ) : groups.length === 0 ? (
              <div className="text-center py-16" data-testid="teams-empty">
                <Users size={40} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50 font-light">Team rosters are being finalised. Check back soon.</p>
              </div>
            ) : (
              <div className="space-y-16" data-testid="teams-groups">
                {groups.map((g) => (
                  <div key={g.season} data-testid={`team-season-${g.season}`}>
                    <div className="flex items-baseline justify-between border-b border-gold/30 pb-3 mb-8">
                      <h2 className="font-display text-2xl sm:text-3xl text-white">{g.season}</h2>
                      <span className="cond-font uppercase tracking-widest text-xs text-gold">{g.players.length} player{g.players.length === 1 ? "" : "s"}</span>
                    </div>
                    {view === "formation" ? <FormationView players={g.players} /> : <ListView players={g.players} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </SettingsProvider>
  );
}
